import {
  SENTILINK_IDENTITY_DISPLAY_NAME,
  SENTILINK_NO_RISK_RAISING_SIGNALS_COPY,
} from "shared/components/vega/ApplicantSummaryV2/sentilinkIdentityLines";

export const SENTILINK_RISK_TIER_LABELS = {
  LOW_RISK: "Low Risk",
  NEEDS_REVIEW: "Needs Review",
  HIGH_RISK: "High Risk",
};

export const SENTILINK_BADGE_LABELS = {
  LOW_RISK: "LOW RISK",
  NEEDS_REVIEW: "NEEDS REVIEW",
  HIGH_RISK: "HIGH RISK",
  ERROR: "ERROR",
  NOT_STARTED: "NOT STARTED",
  SKIPPED_NO_SSN: "SKIPPED — NO SSN",
  MANUALLY_VERIFIED: "Manually verified",
};

export const SENTILINK_NO_REASONS_COPY = "No reasons were returned.";
export { SENTILINK_NO_RISK_RAISING_SIGNALS_COPY };
export const SENTILINK_ERROR_COPY =
  "This request could not be completed. The applicant can retry it from their application.";

const NOT_TRIGGERED_PRESENTATION = {
  NOT_STARTED: {
    statusSuffix: "Not Started",
    badgeLabel: SENTILINK_BADGE_LABELS.NOT_STARTED,
  },
  SKIPPED_NO_SSN: {
    statusSuffix: "Skipped — No SSN",
    badgeLabel: SENTILINK_BADGE_LABELS.SKIPPED_NO_SSN,
  },
};

const RISK_TIERS_WITH_REASONS = new Set(["NEEDS_REVIEW", "HIGH_RISK"]);

export function formatSentilinkAccordionTitle(statusSuffix) {
  return `${SENTILINK_IDENTITY_DISPLAY_NAME} — ${statusSuffix}`;
}

/**
 * @param {import("manager/hooks/application-summary/mappers/interfaces").SentilinkData | undefined} sentilink
 */
export function getSentilinkAccordionPresentation(sentilink) {
  if (!sentilink) {
    return null;
  }

  if (!sentilink.triggered) {
    const notTriggeredKey = sentilink.notTriggeredStatus ?? "NOT_STARTED";
    const notTriggered =
      NOT_TRIGGERED_PRESENTATION[notTriggeredKey] ??
      NOT_TRIGGERED_PRESENTATION.NOT_STARTED;

    return {
      tileClass: "",
      statusSuffix: notTriggered.statusSuffix,
      badgeLabel: notTriggered.badgeLabel,
      riskLevelValue: null,
      valueColorKey: null,
      showReasons: false,
      defaultCollapsed: true,
      statusIconKey: null,
      isManuallyVerified: false,
      isError: false,
    };
  }

  const isError = sentilink.outcome === "ERROR";

  if (isError) {
    return {
      tileClass: "unverified",
      statusSuffix: "Error",
      badgeLabel: SENTILINK_BADGE_LABELS.ERROR,
      riskLevelValue: "Error",
      valueColorKey: "unverified",
      showReasons: false,
      defaultCollapsed: false,
      statusIconKey: "unverified",
      riskTier: null,
      isManuallyVerified: false,
      isError: true,
    };
  }

  if (sentilink.identityManuallyVerified) {
    return {
      tileClass: "verified",
      statusSuffix: "Manually verified",
      badgeLabel: SENTILINK_BADGE_LABELS.MANUALLY_VERIFIED,
      riskLevelValue: "Manually verified",
      valueColorKey: "verified",
      showReasons: false,
      defaultCollapsed: false,
      statusIconKey: "verified",
      riskTier: null,
      isManuallyVerified: true,
      isError: false,
    };
  }

  const { riskTier } = sentilink;
  const statusSuffix = SENTILINK_RISK_TIER_LABELS[riskTier] || "Unknown";
  const badgeLabel =
    SENTILINK_BADGE_LABELS[riskTier] || SENTILINK_BADGE_LABELS.ERROR;

  if (riskTier === "LOW_RISK") {
    return {
      tileClass: "verified",
      statusSuffix,
      badgeLabel,
      riskLevelValue: SENTILINK_RISK_TIER_LABELS.LOW_RISK,
      valueColorKey: "verified",
      showReasons: false,
      defaultCollapsed: false,
      statusIconKey: "verified",
      riskTier,
      isManuallyVerified: false,
      isError: false,
    };
  }

  if (riskTier === "NEEDS_REVIEW") {
    return {
      tileClass: "warn",
      statusSuffix,
      badgeLabel,
      riskLevelValue: SENTILINK_RISK_TIER_LABELS.NEEDS_REVIEW,
      valueColorKey: "warn",
      showReasons: RISK_TIERS_WITH_REASONS.has(riskTier),
      defaultCollapsed: false,
      statusIconKey: "warn",
      riskTier,
      isManuallyVerified: false,
      isError: false,
    };
  }

  if (riskTier === "HIGH_RISK") {
    return {
      tileClass: "unverified",
      statusSuffix,
      badgeLabel,
      riskLevelValue: SENTILINK_RISK_TIER_LABELS.HIGH_RISK,
      valueColorKey: "unverified",
      showReasons: RISK_TIERS_WITH_REASONS.has(riskTier),
      defaultCollapsed: false,
      statusIconKey: "unverified",
      riskTier,
      isManuallyVerified: false,
      isError: false,
    };
  }

  return {
    tileClass: "unverified",
    statusSuffix: "Error",
    badgeLabel: SENTILINK_BADGE_LABELS.ERROR,
    riskLevelValue: "Error",
    valueColorKey: "unverified",
    showReasons: false,
    defaultCollapsed: false,
    statusIconKey: "unverified",
    riskTier: null,
    isManuallyVerified: false,
    isError: true,
  };
}

export function getReasonDescriptionsFromCodes(reasonCodes) {
  if (!reasonCodes?.length) {
    return [];
  }
  return reasonCodes
    .map((rc) =>
      typeof rc?.description === "string" ? rc.description.trim() : ""
    )
    .filter(Boolean);
}

export function getSentilinkReasonDescriptions(sentilink) {
  return getReasonDescriptionsFromCodes(sentilink?.reasonCodes);
}

export function getSentilinkSyntheticReasonDescriptions(sentilink) {
  return getReasonDescriptionsFromCodes(sentilink?.syntheticReasonCodes);
}

export function formatSentilinkCheckReasonLabel(checkName, count) {
  const noun = count === 1 ? "reason" : "reasons";
  return `${checkName} (${count} ${noun})`;
}

export function formatSentilinkEmptyCheckCopy(checkName) {
  return `${checkName}: ${SENTILINK_NO_RISK_RAISING_SIGNALS_COPY}`;
}

export function getSentilinkCheckReasonGroups(sentilink) {
  return [
    {
      key: "id-theft",
      label: "ID Theft",
      descriptions: getSentilinkReasonDescriptions(sentilink),
    },
    {
      key: "synthetic",
      label: "Synthetic",
      descriptions: getSentilinkSyntheticReasonDescriptions(sentilink),
    },
  ];
}
